import { gql, useMutation } from "@apollo/client";
import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useContext, useState } from "react";
import { loggedInUser } from "src/apollo/cache";
import { ModalContext } from "src/context";
import { MixpanelActions } from "src/services/mixpanel";
import styled from "styled-components";
import { restAPI } from "../../apollo";
import { IIntegrations, IntegrationItem } from "../../types/StateTypes";
import { getIntegrationLabel } from "../../utils/format";
import { theme } from "../../utils/theme";
import { appToast } from "../../utils/toast";
import { BACKEND_URL } from "../../utils/variables";
import { FlexDiv } from "../UI";
import { ConnectButton } from "./IntegrationCard/ConnectButton";
import InfoSection from "./IntegrationCard/InfoSection";
import LearnMoreButton from "./IntegrationCard/LearnMoreButton";

interface UserSettingsIntegrationCardProps {
  integration: IntegrationItem;
  isConnected: boolean;
  zoomIsEnabledForOrg: boolean;
  disabled?: boolean;
}

const INTEGRATIONS_THAT_NEED_OFF_SITE_PROCESSING = [
  IIntegrations.Zoom,
  IIntegrations.GoogleCalendar,
  IIntegrations.Google,
  IIntegrations.Gmail,
  IIntegrations.Outlook,
];

const userSettingsGetGmailLink = async () => {
  try {
    const api_call = await restAPI
      .get(`${BACKEND_URL}/gmail/getGmailUrl`)
      .then((response) => {
        if (!!response?.data) {
          const loc = response.data.split("OK. Redirecting to ").join("");
          window.open(loc, "_blank");
        }
      })
      .catch((error) => console.log("error here: ", JSON.stringify(error)));
    return api_call;
  } catch (error: any) {
    appToast("Error connecting to Gmail. Please try again later.");
  }
};

const userSettingsSendHubSpotCode = async () => {
  try {
    const api_call = await restAPI
      .get(`${BACKEND_URL}/hubspot/redirect/`)
      .then((response) => {
        if (!!response?.data) {
          const loc = response.data.split("OK. Redirecting to ").join("");
          window.open(loc, "_blank");
        }
      })
      .catch((error) => console.log("error here: ", JSON.stringify(error)));

    return api_call;
  } catch (error: any) {
    appToast("Error connecting to HubSpot. Please try again later.");
  }
};

const userSettingsLinkGoogleMeet = async (link: string) => {
  try {
    const api_call = await restAPI
      .get(link)
      .then((response) => {
        if (!!response?.data) {
          const loc = response.data.split("OK. Redirecting to ").join("");
          window.open(loc, "_blank");
        }
      })
      .catch((error) => console.log("error here: ", JSON.stringify(error)));
    return api_call;
  } catch (error: any) {
    appToast("Error connecting to Google Meet. Please try again later.");
  }
};

const userSettingsLinkGoogle = async () => {
  try {
    const api_call = await restAPI.get(`${BACKEND_URL}/google/getGoogleUrl`).then((response) => {
      if (!!response?.data) {
        const loc = response.data.split("OK. Redirecting to ").join("");
        window.open(loc, "_blank");
      }
    });
  } catch (error: any) {
    appToast("Error connecting to Google. Please try again later.");
  }
};

const userSettingsLinkNoTimeKitOutlook = async () => {
  try {
    const api_call = await restAPI.get(`${BACKEND_URL}/outlook/redirect`).then((response) => {
      if (!!response?.data) {
        const loc = response.data.split("OK. Redirecting to ").join("");
        window.open(loc, "_blank");
      }
    });
  } catch (error: any) {
    appToast("Error connecting to Outlook. Please try again later.");
  }
};

const userSettingsGetTooltipText = (
  integration: IntegrationItem,
  switchStateIsConnected: boolean,

  zoomIsEnabledForOrg: boolean,
) => {
  if (!switchStateIsConnected) {
    switch (integration.name) {
      case IIntegrations.Zoom:
        return zoomIsEnabledForOrg
          ? "Zoom is not enabled on your account. Please contact an enablement user to enable this integration."
          : "Please contact an enablement user to enable this integration.";
      default:
        return undefined;
    }
  }

  switch (integration.name) {
    case "Gmail":
      return undefined;
    case IIntegrations.GoogleCalendar:
      return "Please contact support to disconnect this integration.";
    case IIntegrations.Outlook:
      return "Please contact support to disconnect this integration.";
    case IIntegrations.Zoom:
      return "You have Zoom enabled on your account. Please contact an enablement user to disable this integration.";
    default:
      return undefined;
  }
};

const userSettingsGetDisabledState = (integration: IntegrationItem, isConnected: boolean) => {
  switch (integration.name) {
    // never able to be toggled OFF by user
    case IIntegrations.GoogleCalendar:
      return !!isConnected;
    // must be added to org by admin
    case IIntegrations.Zoom:
      return true;
    default:
      return integration.disabled;
  }
};

const UserSettingsIntegrationCard: React.FC<UserSettingsIntegrationCardProps> = ({
  integration,
  isConnected,
  zoomIsEnabledForOrg,
}) => {
  const [switchStateIsConnected, setSwitchStateIsConnected] = useState(isConnected);

  const {
    showDisconnectIntegrationModal,
    setShowDisconnectIntegrationModal,
    selectedIntegrationDisconnect,
    setSelectedIntegrationDisconnect,
  } = useContext(ModalContext);

  const handleSwitchChange = async (checked: boolean) => {
    const organization_id = localStorage.getItem("organization_id");

    if (checked) {
      if (!INTEGRATIONS_THAT_NEED_OFF_SITE_PROCESSING.includes(integration.name)) {
        setSwitchStateIsConnected(true);
      }

      switch (integration.name) {
        case "Gmail":
          await userSettingsGetGmailLink();
          break;
        case IIntegrations.HubSpot:
          await userSettingsSendHubSpotCode();
          break;
        case IIntegrations.Zoom:
          // Reps are unable to alter these settings
          // the switch is always disabled
          break;
        case IIntegrations.GoogleMeet:
          await userSettingsLinkGoogleMeet(integration?.url || "");
          break;
        case IIntegrations.GoogleCalendar:
          window.open(`${integration.url}?organization_id=${organization_id}`);
          break;
        case IIntegrations.Outlook:
          if (timekitReplacementOrg) {
            await userSettingsLinkNoTimeKitOutlook();
          } else {
            window.open(`${integration.url}?organization_id=${organization_id}`);
          }
          break;
        case IIntegrations.Google:
          await userSettingsLinkGoogle();
          break;
        default:
          window.open(`${integration.url}?organization_id=${organization_id}`);
      }
      MixpanelActions.track("Integration Connected", { integration: integration.name });
    } else {
      setSelectedIntegrationDisconnect(integration.name);
      setShowDisconnectIntegrationModal(true);
    }
  };

  const timekitReplacementOrg = !!loggedInUser().organization?.replace_timekit;

  return (
    <Main isConnected={isConnected}>
      <Header>
        <IntegrationLogo
          src={integration.logo}
          width="32px"
          alt={`${getIntegrationLabel(integration.name, timekitReplacementOrg)} logo`}
        />
        <ConnectButton
          integration={integration}
          isConnected={switchStateIsConnected}
          disabled={userSettingsGetDisabledState(integration, switchStateIsConnected)}
          tooltipText={userSettingsGetTooltipText(integration, switchStateIsConnected, zoomIsEnabledForOrg)}
          onChange={handleSwitchChange}
        />
      </Header>
      <InfoSection integration={integration} timekitReplacementOrg={timekitReplacementOrg} />

      <FlexDiv
        gap={8}
        align="center"
        justify="space-between"
        style={{
          overflow: "visible",
          position: "relative",
          minHeight: "32px",
        }}
      >
        {integration.learnMoreUrl && <LearnMoreButton integration={integration} />}
      </FlexDiv>
    </Main>
  );
};

const Main = styled.div<{ isConnected: boolean }>`
  width: 257px;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid
    ${({ isConnected }) => (isConnected ? theme.border.neutral.primary : theme.border.neutral.secondary)};
  display: flex;
  justify-content: space-around;
  flex-direction: column;
  gap: 8px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const IntegrationLogo = styled.img`
  display: flex;
  flex-direction: row;
`;

export { UserSettingsIntegrationCard };
